import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@wove/web-ui'
import {UserWithRole} from '@wove/api/types'
import { Trash2 } from 'lucide-react'
import { useAuth } from '@src/hooks/useAuth'
import { useDeactivateUsersMutation } from '@wove/api'
import { useCallback, useState } from 'react'
import { useRequiredRoles } from '@src/hooks/useRequiredRoles'
import { Role } from '@wove/common/roles'

const getStatusBadge = (status: 'active' | 'inactive' | 'invited') => {
  switch (status) {
    case 'active':
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          Active
        </Badge>
      )
    case 'invited':
      return (
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
          Invited
        </Badge>
      )
    case 'inactive':
      return (
        <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
          Inactive
        </Badge>
      )
    default:
      return null
  }
}

const UserRow = ({
  member,
  changeRole,
}: {
  member: UserWithRole
  changeRole: (id: string, role: string) => void
}) => {
  const auth = useAuth()
  const { hasRole } = useRequiredRoles([Role.ADMIN])
  const [, executeDeactivate] = useDeactivateUsersMutation()
  const [showRoleChangeDialog, setShowRoleChangeDialog] = useState(false)
  const [pendingRole, setPendingRole] = useState<string | null>(null)

  const onDeactivate = useCallback(() => {
    executeDeactivate({ userId: member.id })
  }, [executeDeactivate, member.id])

  // Can edit role if user is admin and it's not their own account
  const canEditRole = hasRole && member.id !== auth.user?.id && !member.deactivatedAt

  const handleRoleChange = (newRole: string) => {
    setPendingRole(newRole)
    setShowRoleChangeDialog(true)
  }

  const confirmRoleChange = () => {
    if (pendingRole) {
      changeRole(member.id, pendingRole)
      setShowRoleChangeDialog(false)
      setPendingRole(null)
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Admin'
      case 'core':
        return 'Core'
      case 'general':
        return 'General'
      default:
        return role
    }
  }

  return (
    <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg">
      <div className="flex items-center space-x-4">
        <Avatar>
          <AvatarImage src={''} alt={member.firstName + ' ' + member.lastName} />
          <AvatarFallback>{member.firstName[0] + member.lastName?.[0] || ''}</AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium">
            {member.firstName} {member.lastName}
          </p>
          <p className="text-sm text-muted-foreground">{member.email}</p>
        </div>
      </div>
      <div className="flex items-center space-x-4">
        {getStatusBadge(member.deactivatedAt ? 'inactive' : 'active')}
        <Select value={member.role} onValueChange={handleRoleChange} disabled={!canEditRole}>
          <SelectTrigger className="w-[110px]" disabled={!canEditRole}>
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="core">Core</SelectItem>
            <SelectItem value="general">General</SelectItem>
          </SelectContent>
        </Select>
        {member.id !== auth.user?.id && !member.deactivatedAt && (
          <Button variant="ghost" size="icon" onClick={onDeactivate}>
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>

      <Dialog open={showRoleChangeDialog} onOpenChange={setShowRoleChangeDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader className="space-y-3">
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to change {member.firstName} {member.lastName}'s role from{' '}
              <span className="font-semibold">{getRoleLabel(member.role)}</span> to{' '}
              <span className="font-semibold">{pendingRole && getRoleLabel(pendingRole)}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowRoleChangeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmRoleChange}>Confirm Change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default UserRow
